import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, Instagram } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/lib/site";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${scrolled ? "glass-header" : "bg-transparent"}`}
    >
      <div className="container-page flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <img
            src="/logo.png"
            alt="ME Builders"
            className="h-14 md:h-20 w-auto rounded-sm"
            loading="eager"
          />
        </Link>

        {/* Desktop nav */}
        <nav data-testid="primary-nav" className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <DropdownMenu key={link.to}>
                <DropdownMenuTrigger
                  data-testid={`nav-${link.label.toLowerCase()}-trigger`}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-brand-ink hover:text-brand-olive-hover transition-colors"
                >
                  {link.label}
                  <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 rounded-md">
                  <DropdownMenuItem asChild>
                    <Link to={link.to} data-testid="nav-services-overview" className="cursor-pointer">
                      All Services
                    </Link>
                  </DropdownMenuItem>
                  {link.children.map((c) => (
                    <DropdownMenuItem key={c.to} asChild>
                      <Link to={c.to} data-testid={`nav-${c.to.split('/').pop()}`} className="cursor-pointer">
                        {c.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "text-brand-obsidian" : "text-brand-ash hover:text-brand-ink"
                  }`
                }
              >
                {link.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="header-instagram"
            href={BUSINESS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="ME Builders on Instagram"
            className="hidden md:inline-flex items-center justify-center w-10 h-10 rounded-sm border border-brand-line bg-white hover:bg-brand-limestone hover:border-brand-obsidian transition-colors text-brand-ink"
          >
            <Instagram className="w-4 h-4" strokeWidth={1.75} />
          </a>
          <a
            data-testid="header-call-cta"
            href={BUSINESS.phoneHref}
            className="hidden md:inline-flex items-center gap-2 btn-primary"
          >
            <Phone className="w-4 h-4" strokeWidth={2} /> {BUSINESS.phone}
          </a>

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="lg:hidden p-2 rounded-sm hover:bg-brand-limestone transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6 text-brand-ink" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] sm:w-[420px] bg-brand-bone border-brand-line">
              <SheetTitle className="sr-only">ME Builders Mobile Menu</SheetTitle>
              <div className="flex flex-col h-full pt-2">
                <div className="flex items-center justify-between mb-8">
                  <img src="/logo.png" alt="ME Builders" className="h-14 w-auto rounded-sm" />
                  <button onClick={() => setOpen(false)} className="p-2" aria-label="Close menu">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <nav className="flex flex-col gap-1 flex-1 overflow-y-auto">
                  {NAV_LINKS.map((link) => (
                    <div key={link.to}>
                      <Link
                        to={link.to}
                        data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                        className="block py-3 border-b border-brand-line font-heading text-2xl font-bold text-brand-ink"
                      >
                        {link.label}
                      </Link>
                      {link.children && (
                        <div className="pl-4 py-2 flex flex-col gap-2">
                          {link.children.map((c) => (
                            <Link
                              key={c.to}
                              to={c.to}
                              data-testid={`mobile-nav-${c.to.split('/').pop()}`}
                              className="text-sm text-brand-ash hover:text-brand-ink"
                            >
                              {c.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="mobile-call-cta"
                  className="btn-primary mt-6 w-full"
                >
                  <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
                </a>
                <a
                  href={BUSINESS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-instagram"
                  className="btn-secondary mt-3 w-full"
                >
                  <Instagram className="w-4 h-4" /> Follow {BUSINESS.instagramHandle}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
